import React, { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import {
  FileCheck, Camera, Upload, Eye, Trash2, RefreshCw, Info,
  Award, CreditCard, Building2, Home, FileText, Zap,
  CheckCircle2, XCircle, Clock, Save, ArrowLeft, ArrowRight
} from 'lucide-react';

const DOC_TYPES = [
  { type: 'business_license', label: 'Business License', required: true, description: 'Upload your business license document', icon: Award, iconBg: 'bg-gray-100', iconColor: 'text-gray-500' },
  { type: 'voided_check', label: 'Voided Business Check', required: true, description: 'Bank letter acceptable alternative', icon: CreditCard, iconBg: 'bg-green-100', iconColor: 'text-green-600' },
  { type: 'driver_license_front', label: 'Driver License (Front)', required: true, description: "Owner's identification front side", icon: CreditCard, iconBg: 'bg-teal-100', iconColor: 'text-teal-600' },
  { type: 'driver_license_back', label: 'Driver License (Back)', required: true, description: "Owner's identification back side", icon: CreditCard, iconBg: 'bg-teal-100', iconColor: 'text-teal-600' },
  { type: 'business_exterior', label: 'Business Exterior', required: true, description: 'Photo of outside of business', icon: Building2, iconBg: 'bg-amber-100', iconColor: 'text-amber-600' },
  { type: 'business_interior', label: 'Business Interior', required: true, description: 'Photo of inside of business', icon: Home, iconBg: 'bg-gray-100', iconColor: 'text-gray-500' },
  { type: 'utility_bill', label: 'Utility Bill', required: false, description: 'Optional supporting document', icon: Zap, iconBg: 'bg-gray-50', iconColor: 'text-gray-400' }
];

function Step5Documents({ application, onNext, onPrevious, saving, onSaveDraft }) {
  const section = application.sections?.find(s => s.step_number === 5) || {};
  const [documents, setDocuments] = useState(section.data?.documents || {});
  const [uploading, setUploading] = useState(null);

  const uploadedCount = useMemo(() => Object.keys(documents).filter(k => documents[k]?.status === 'uploaded' || documents[k]?.status === 'pending').length, [documents]);
  const totalDocs = DOC_TYPES.length;
  const progressPct = (uploadedCount / totalDocs) * 100;

  const getDocStatus = (type) => documents[type]?.status || 'missing';

  const handleFileSelect = async (docType, file) => {
    if (!file) return;
    setUploading(docType);
    // Simulate upload delay for now, real upload would use object storage
    await new Promise(r => setTimeout(r, 800));
    setDocuments(prev => ({
      ...prev,
      [docType]: { status: 'uploaded', filename: file.name, size: file.size, uploadedAt: new Date().toISOString() }
    }));
    setUploading(null);
  };

  const handleDelete = (docType) => {
    setDocuments(prev => {
      const next = { ...prev };
      delete next[docType];
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext({ documents });
  };

  const statusConfig = {
    uploaded: { color: 'border-green-300 bg-green-50/50', badge: 'bg-green-100 text-green-700', icon: CheckCircle2, iconColor: 'text-green-500', label: 'Uploaded' },
    pending: { color: 'border-amber-300 bg-amber-50/50', badge: 'bg-amber-100 text-amber-700', icon: Clock, iconColor: 'text-amber-500', label: 'Pending' },
    missing: { color: 'border-dashed border-gray-300', badge: 'bg-red-100 text-red-600', icon: XCircle, iconColor: 'text-red-400', label: 'Missing' }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#3b82f6] flex items-center justify-center shrink-0">
          <FileCheck className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold" data-testid="step5-title">Document Upload</h3>
          <p className="text-sm text-muted-foreground">Please upload the required supporting documents</p>
        </div>
      </div>

      {/* Upload Progress */}
      <div className="space-y-1.5" data-testid="upload-progress">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-muted-foreground">Upload Progress</span>
          <span className="font-semibold">{uploadedCount} of {totalDocs} completed</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="bg-[#3b82f6] h-2 rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Document Cards Grid */}
      <div className="grid gap-4 md:grid-cols-2">
        {DOC_TYPES.map(doc => {
          const status = getDocStatus(doc.type);
          const cfg = statusConfig[status] || statusConfig.missing;
          const isUploading = uploading === doc.type;
          const Icon = doc.icon;
          const StatusIcon = cfg.icon;

          return (
            <div key={doc.type} className={`border-2 rounded-xl p-5 transition-all ${cfg.color} ${doc.required ? '' : 'opacity-80'}`} data-testid={`doc-card-${doc.type}`}>
              <div className="flex flex-col items-center text-center space-y-3">
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl ${doc.iconBg} flex items-center justify-center`}>
                  {status === 'uploaded' ? (
                    <CheckCircle2 className="h-6 w-6 text-green-500" />
                  ) : status === 'pending' ? (
                    <Clock className="h-6 w-6 text-amber-500" />
                  ) : (
                    <Icon className={`h-6 w-6 ${doc.iconColor}`} />
                  )}
                </div>

                {/* Title */}
                <div>
                  <div className="font-semibold text-sm">
                    {doc.label} {doc.required && <span className="text-red-500">*</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">{doc.description}</div>
                </div>

                {/* Upload spinner */}
                {isUploading && (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3b82f6]" />
                )}

                {/* Action Buttons */}
                {status === 'uploaded' || status === 'pending' ? (
                  <div className="grid grid-cols-2 gap-2 w-full">
                    <Button type="button" variant="outline" size="sm" className="text-xs" data-testid={`preview-${doc.type}`}>
                      <Eye className="h-3 w-3 mr-1" /> Preview
                    </Button>
                    {status === 'pending' ? (
                      <Button type="button" size="sm" className="text-xs bg-[#3b82f6]" onClick={() => document.getElementById(`file-${doc.type}`)?.click()}>
                        <RefreshCw className="h-3 w-3 mr-1" /> Replace
                      </Button>
                    ) : (
                      <Button type="button" variant="outline" size="sm" className="text-xs text-red-500 border-red-200 hover:bg-red-50" onClick={() => handleDelete(doc.type)} data-testid={`delete-${doc.type}`}>
                        <Trash2 className="h-3 w-3 mr-1" /> Delete
                      </Button>
                    )}
                  </div>
                ) : (
                  !isUploading && (
                    <div className="space-y-2 w-full">
                      <Button type="button" size="sm" className="w-full text-xs bg-[#3b82f6] hover:bg-[#2563eb]" onClick={() => document.getElementById(`file-${doc.type}`)?.click()}>
                        <Camera className="h-3 w-3 mr-1" /> Take Photo
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="w-full text-xs" onClick={() => document.getElementById(`file-${doc.type}`)?.click()} data-testid={`upload-${doc.type}`}>
                        <Upload className="h-3 w-3 mr-1" /> Choose File
                      </Button>
                    </div>
                  )
                )}

                {/* Hidden file input */}
                <input type="file" id={`file-${doc.type}`} onChange={(e) => handleFileSelect(doc.type, e.target.files[0])} className="hidden" accept=".pdf,.jpg,.jpeg,.png" />

                {/* Status Badge */}
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${cfg.badge}`}>
                  <StatusIcon className={`h-3 w-3 ${cfg.iconColor}`} />
                  {doc.required ? cfg.label : 'Optional'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* File Requirements */}
      <div className="border rounded-xl p-4 bg-blue-50/40">
        <div className="flex items-start gap-2">
          <Info className="h-5 w-5 text-[#3b82f6] mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-sm">File Requirements</div>
            <ul className="text-xs text-muted-foreground mt-1 space-y-0.5 list-disc list-inside">
              <li>Maximum file size: 10MB per document</li>
              <li>Accepted formats: JPG, PNG, PDF</li>
              <li>Images should be clear and readable</li>
              <li>All required documents must be uploaded to proceed</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center pt-4 border-t">
        <Button type="button" variant="outline" onClick={() => onSaveDraft?.({ documents })} data-testid="save-draft-btn">
          <Save className="h-4 w-4 mr-2" /> Exit & Save Draft
        </Button>
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" onClick={onPrevious} data-testid="previous-step-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <Button type="submit" disabled={saving} className="bg-[#3b82f6] hover:bg-[#2563eb]" data-testid="save-continue-btn">
            {saving ? 'Saving...' : 'Save & Continue \u2192'}
          </Button>
        </div>
      </div>
    </form>
  );
}

export default Step5Documents;
